import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { HelpButton } from '../components/HelpButton.js';
import { PageTitle } from '../components/Layouts.js';
import { useLocale } from '../components/site.js';
import {
  Button,
  Card,
  LoadError,
  Loading,
  Notice,
  SelectField,
  SeverityBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';
import { SEVERITIES } from './Overview.js';

export const CATEGORIES = [
  'availability',
  'security',
  'links',
  'seo',
  'performance',
  'email',
  'compliance',
] as const;
type Category = (typeof CATEGORIES)[number];
type Status = 'open' | 'ignored' | 'resolved';
type IgnoreReason = 'not_relevant' | 'will_fix_later' | 'false_positive';
const REASONS: IgnoreReason[] = ['not_relevant', 'will_fix_later', 'false_positive'];

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | undefined =>
  allowed.find((a) => a === value);

/** One occurrence: the affected address with ignore / restore. */
function Occurrence({ finding, status }: { finding: Schemas['Finding']; status: Status }) {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const reasonId = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<IgnoreReason>('not_relevant');
  const change = useMutation({
    mutationFn: (body: { status: 'ignored' | 'open'; ignoreReason?: IgnoreReason }) =>
      unwrap(
        api.PATCH('/findings/{findingId}', {
          params: { path: { findingId: finding.id } },
          body,
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
  return (
    <li className="flex flex-col gap-2 border-t border-slate-100 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-all">{finding.affectedUrl ?? t('findings.wholeSite')}</span>
        {status === 'open' && !asking ? (
          <Button variant="secondary" onClick={() => setAsking(true)}>
            {t('findings.ignore')}
          </Button>
        ) : null}
        {status === 'ignored' ? (
          <Button
            variant="secondary"
            busy={change.isPending}
            onClick={() => change.mutate({ status: 'open' })}
          >
            {t('findings.restore')}
          </Button>
        ) : null}
      </div>
      {asking ? (
        <form
          className="flex flex-col gap-3 rounded-md bg-slate-50 p-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ status: 'ignored', ignoreReason: reason });
          }}
        >
          <SelectField
            id={reasonId}
            label={t('findings.ignoreReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value as IgnoreReason)}
          >
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`findings.reasons.${r}`)}
              </option>
            ))}
          </SelectField>
          <Button type="submit" busy={change.isPending}>
            {t('findings.ignoreConfirm')}
          </Button>
          <Button variant="secondary" onClick={() => setAsking(false)}>
            {t('app.cancel')}
          </Button>
        </form>
      ) : null}
      {finding.ignoreReason && status === 'ignored' ? (
        <p className="text-sm text-slate-700">{t(`findings.reasons.${finding.ignoreReason}`)}</p>
      ) : null}
      {change.isError ? <Notice kind="error">{errorMessage(change.error, t)}</Notice> : null}
    </li>
  );
}

/** A problem card: what, impact, action, how to fix it yourself, help from us. */
function GroupCard({
  group,
  status,
  initiallyOpen,
}: {
  group: Schemas['FindingGroup'];
  status: Status;
  initiallyOpen: boolean;
}) {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [open, setOpen] = useState(initiallyOpen);
  const panelId = useId();
  const e = group.explanation;
  const since = group.items.reduce<string | null>(
    (min, f) => (min === null || f.firstSeenAt < min ? f.firstSeenAt : min),
    null,
  );
  return (
    <li>
      <Card>
        <h2 className="text-lg font-semibold">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => {
              if (!open) track('finding_viewed', { ruleCode: group.ruleCode });
              setOpen(!open);
            }}
            className="flex min-h-11 w-full flex-wrap items-center gap-2 text-left"
          >
            <SeverityBadge severity={group.severity} />
            <span className="min-w-0 flex-1">{group.title}</span>
            {group.count > 1 ? (
              <span className="text-sm font-normal text-slate-700">
                {t('findings.count', { count: group.count })}
              </span>
            ) : null}
            <span aria-hidden="true">{open ? '▲' : '▼'}</span>
          </button>
        </h2>
        <div id={panelId} hidden={!open} className="mt-3 flex flex-col gap-3">
          {since ? (
            <p className="text-sm text-slate-700">
              {t('findings.since', { date: formatDate(since, i18n.language, timeZone) })}
            </p>
          ) : null}
          {e ? (
            <dl className="flex flex-col gap-2 wrap-anywhere">
              <div>
                <dt className="font-semibold">{t('findings.what')}</dt>
                <dd>{e.what}</dd>
              </div>
              <div>
                <dt className="font-semibold">{t('findings.impact')}</dt>
                <dd>{e.impact}</dd>
              </div>
              <div>
                <dt className="font-semibold">{t('findings.action')}</dt>
                <dd>{e.action}</dd>
              </div>
            </dl>
          ) : null}
          {e?.legalDisclaimer ? <Notice kind="info">{e.legalDisclaimer}</Notice> : null}
          {e && e.diySteps.length > 0 ? (
            <details
              className="rounded-md border border-slate-200 p-3"
              onToggle={(e) => {
                if (e.currentTarget.open) track('diy_steps_opened', { ruleCode: group.ruleCode });
              }}
            >
              <summary className="min-h-11 cursor-pointer py-2 font-semibold text-blue-900">
                {t('findings.diy')}
              </summary>
              <ol className="ml-5 list-decimal">
                {e.diySteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </details>
          ) : null}
          {group.service && status === 'open' ? (
            <p>
              <HelpButton
                siteId={siteId}
                service={group.service}
                {...(group.items[0] ? { findingId: group.items[0].id } : {})}
                ruleCode={group.ruleCode}
              />
            </p>
          ) : null}
          <div>
            <h3 className="font-semibold">{t('findings.affected')}</h3>
            <ul>
              {group.items.map((f) => (
                <Occurrence key={f.id} finding={f} status={status} />
              ))}
            </ul>
            {group.count > group.items.length ? (
              <p className="text-sm text-slate-700">
                {t('findings.more', { count: group.count - group.items.length })}
              </p>
            ) : null}
          </div>
        </div>
      </Card>
    </li>
  );
}

/** S4 "Probleme": open problems grouped by rule, most severe first. */
export function Findings() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const [params, setParams] = useSearchParams();
  const category = oneOf(params.get('category'), CATEGORIES);
  const severity = oneOf(params.get('severity'), SEVERITIES);
  const status = oneOf(params.get('status'), ['open', 'ignored', 'resolved'] as const) ?? 'open';
  const openRule = params.get('open');
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('open');
    setParams(next, { replace: true });
  };
  const findings = useQuery({
    queryKey: ['site', siteId, 'findings', category, severity, status, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: {
            path: { siteId },
            query: {
              grouped: 'true',
              status,
              locale,
              limit: 200,
              ...(category ? { category } : {}),
              ...(severity ? { severity } : {}),
            },
          },
        }),
      ),
  });
  const groups = findings.data?.groups ?? [];

  return (
    <>
      <PageTitle
        actions={
          <Link
            to={`/sites/${siteId}/report`}
            className="inline-flex min-h-11 items-center rounded-md border border-slate-300 bg-white px-4 font-semibold text-slate-900 no-underline"
          >
            {t('report.open')}
          </Link>
        }
      >
        {t('findings.title')}
      </PageTitle>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <SelectField
          label={t('findings.filters.status')}
          value={status}
          onChange={(e) => setFilter('status', e.target.value === 'open' ? '' : e.target.value)}
        >
          {(['open', 'ignored', 'resolved'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`findings.statuses.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('findings.filters.category')}
          value={category ?? ''}
          onChange={(e) => setFilter('category', e.target.value)}
        >
          <option value="">{t('findings.filters.all')}</option>
          {CATEGORIES.map((c: Category) => (
            <option key={c} value={c}>
              {t(`site.tiles.${c}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('findings.filters.severity')}
          value={severity ?? ''}
          onChange={(e) => setFilter('severity', e.target.value)}
        >
          <option value="">{t('findings.filters.all')}</option>
          {SEVERITIES.map((s: Severity) => (
            <option key={s} value={s}>
              {t(`severity.${s}`)}
            </option>
          ))}
        </SelectField>
      </div>
      {findings.isPending ? <Loading /> : null}
      {findings.isError ? (
        <LoadError
          message={errorMessage(findings.error, t)}
          onRetry={() => void findings.refetch()}
        />
      ) : null}
      {findings.data && groups.length === 0 ? (
        <Notice kind="success">{t(`findings.empty.${status}`)}</Notice>
      ) : null}
      {groups.length > 0 ? (
        <ul className="flex flex-col gap-4">
          {groups.map((g, i) => (
            <GroupCard
              key={`${g.ruleCode}-${status}`}
              group={g}
              status={status}
              initiallyOpen={openRule ? openRule === g.ruleCode : i === 0}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}
